import { FeeAmountInput } from "@/components/registrant/license-form/fee-amount-input";
import { FeesPaymentScreenQaBlock } from "@/components/registrant/license-form/fees-payment-screen-qa";
import type {
  LicenseFeesForPeriodSchema,
  LicenseFeesResponseSchema,
  LicenseTypeConfigurationResponseSchema,
  QuestionResponseSchema,
} from "@/hera-api/client/types.gen";
import { useRegistrantOrgPermissions } from "@/hooks/use-registrant-org-permissions";
import { isPaymentScreenAttestation } from "@/lib/payment-screen-attestation";
import type { PeriodsAndFeesData } from "@/types/license";
import { useStore } from "@tanstack/react-form";
import { addMonths, format, parse } from "date-fns";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import React from "react";
import z from "zod";
import {
  formatCurrency,
  parseDateAsLocal,
  roundToTwoDecimals,
} from "../../../lib/utils";
import { Alert, AlertDescription } from "../../ui/alert";
import { Button } from "../../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Checkbox } from "../../ui/checkbox";
import ErrorComponent from "../../ui/error";
import { FormFieldWrapper } from "../../ui/form-field-wrapper";
import { Label } from "../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Separator } from "../../ui/separator";
import { useAppForm } from "../../ui/tanstack-form";
import { Textarea } from "../../ui/textarea";

// Stripe fee constants (matching backend)
const ACH_FEE_RATE = 0.008;
const MAX_ACH_FEE_CENTS = 500;
const CREDIT_CARD_FEE_RATE = 0.029;
const CREDIT_CARD_FIXED_FEE_CENTS = 30;

interface PeriodsAndFeesFormProps {
  onPrevious?: () => Promise<void>;
  onSubmit: (
    data: PeriodsAndFeesData,
    paymentMethodType?:
      | "card"
      | "us_bank_account"
      | "check"
      | "invoice"
      | "pay_later"
      | "no_payment",
  ) => Promise<void>;
  stepNumber: number;
  isRenewal: boolean;
  feesData: LicenseFeesResponseSchema;
  configurationData: LicenseTypeConfigurationResponseSchema;
  initialPaymentMethod?:
    | "card"
    | "us_bank_account"
    | "check"
    | "invoice"
    | null;
  isReapply?: boolean;
  effectiveDateLabel?: string;
  inspectionExemptionAnswer?: string; // Date string in YYYY-MM-DD format
  effectiveDate?: string; // Date string in YYYY-MM-DD format
  /** Registration questions + answers for items flagged “show on fees payment screen”. */
  paymentScreenQuestions?: QuestionResponseSchema[] | null;
  paymentScreenAnswers?: Record<string, unknown> | null;
}

interface PeriodsAndFeesFormValues {
  registrant_note: string;
  [key: `${string}-selected`]: boolean;
  [key: `${string}-fee`]: string;
  [key: `${string}-late-fee`]: string;
  [key: `${string}-already-paid`]: string;
  inspection_fee?: string;
}

const feeFieldSchema = z.string().refine(
  (val) => {
    const num = Number.parseFloat(val);
    return !Number.isNaN(num) && num >= 0;
  },
  { message: "Must be a non-negative number" },
);

export function PeriodsAndFeesForm({
  onPrevious,
  onSubmit,
  stepNumber,
  isRenewal,
  feesData,
  configurationData,
  initialPaymentMethod,
  isReapply = false,
  effectiveDateLabel = "vacancy date",
  inspectionExemptionAnswer,
  effectiveDate,
  paymentScreenQuestions,
  paymentScreenAnswers,
}: PeriodsAndFeesFormProps) {
  const { canMakePayments } = useRegistrantOrgPermissions();
  const attestationQuestions = React.useMemo(
    () =>
      (paymentScreenQuestions ?? [])
        .filter(isPaymentScreenAttestation)
        .slice()
        .sort((a, b) => a.display_order - b.display_order),
    [paymentScreenQuestions],
  );
  const recapQuestions = React.useMemo(
    () =>
      paymentScreenQuestions?.filter((q) => !isPaymentScreenAttestation(q)) ??
      null,
    [paymentScreenQuestions],
  );
  const [attested, setAttested] = React.useState<Record<string, boolean>>({});
  const attestationIncomplete = attestationQuestions.some(
    (question) =>
      question.answer_required !== false && attested[question.id] !== true,
  );
  // Ensure the effective date label includes the word "date" (case-insensitive)
  const displayDateLabel = React.useMemo(() => {
    const lowerLabel = effectiveDateLabel.toLowerCase();
    if (lowerLabel.includes("date")) {
      return effectiveDateLabel;
    }
    return `${effectiveDateLabel} date`;
  }, [effectiveDateLabel]);

  /** For renewal: only show periods where fee is due (registration + late - already paid > 0). */
  const feesToShow = React.useMemo(() => {
    const fees = feesData?.fees ?? [];
    if (!isRenewal || feesData.omt_exempt) return fees;
    return fees.filter((fee: LicenseFeesForPeriodSchema) => {
      const reg = Number.parseFloat(String(fee.registration_fee)) || 0;
      const late = Number.parseFloat(String(fee.late_fee)) || 0;
      const paid = Number.parseFloat(String(fee.already_paid_fee ?? "0")) || 0;
      return reg + late - paid > 0;
    });
  }, [feesData?.fees, feesData.omt_exempt, isRenewal]);

  const defaultValues = React.useMemo((): PeriodsAndFeesFormValues => {
    const defaults: PeriodsAndFeesFormValues = {
      registrant_note: "",
    };
    for (const fee of feesToShow) {
      defaults[`${fee.start_date}-selected`] = true;
      defaults[`${fee.start_date}-fee`] = roundToTwoDecimals(
        fee.registration_fee,
      );
      defaults[`${fee.start_date}-late-fee`] = roundToTwoDecimals(fee.late_fee);
      defaults[`${fee.start_date}-already-paid`] =
        fee.already_paid_fee ?? "0.00";
    }
    // Prefill inspection fee if available
    if (
      feesData?.inspection_fee !== null &&
      feesData?.inspection_fee !== undefined
    ) {
      defaults.inspection_fee = roundToTwoDecimals(feesData.inspection_fee);
    } else {
      defaults.inspection_fee = "0.00";
    }
    return defaults;
  }, [feesToShow, feesData?.inspection_fee]);

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      onSubmit({
        periods: feesToShow.map((fee) => {
          return {
            start_date: fee.start_date,
            end_date: fee.end_date,
            fee: value[`${fee.start_date}-selected`]
              ? roundToTwoDecimals(value[`${fee.start_date}-fee`])
              : "0.00",
            late_fee: value[`${fee.start_date}-selected`]
              ? roundToTwoDecimals(value[`${fee.start_date}-late-fee`])
              : "0.00",
            prefilled_fee: fee.registration_fee,
            prefilled_late_fee: fee.late_fee,
            selected: value[`${fee.start_date}-selected`],
          };
        }),
        registrant_note: value.registrant_note,
      });
    },
  });

  const numSelectedPeriods = useStore(form.store, (state) =>
    Object.entries(state.values).filter(([key, value]) => {
      if (!key.endsWith("-selected")) {
        return false;
      }
      return value === true;
    }),
  ).length;
  const fees = useStore(form.store, (state) =>
    Object.entries(state.values).filter(([key]) => {
      if (!key.endsWith("-fee") || key.endsWith("-late-fee")) {
        return false;
      }
      const period = key.split("-fee")[0];
      return state.values[`${period}-selected`] === true;
    }),
  );
  const lateFees = useStore(form.store, (state) =>
    Object.entries(state.values).filter(([key]) => {
      if (!key.endsWith("-late-fee")) {
        return false;
      }
      const period = key.split("-late-fee")[0];
      return state.values[`${period}-selected`] === true;
    }),
  );
  const totalFee = fees.reduce((acc, [, value]) => acc + Number(value ?? 0), 0);
  const totalLateFee = lateFees.reduce(
    (acc, [, value]) => acc + Number(value ?? 0),
    0,
  );

  // Get inspection fee from form
  const inspectionFee = useStore(form.store, (state) => {
    const fee = state.values.inspection_fee;
    return fee ? Number(fee) : 0;
  });

  const subtotal = totalFee + totalLateFee + inspectionFee;

  const [paymentMethod, setPaymentMethod] = React.useState<
    "card" | "us_bank_account" | "check" | "invoice" | null
  >(initialPaymentMethod || "card");

  /** Without the pay grant only Pay Later is offered, so no method is in play. */
  const effectivePaymentMethod = canMakePayments ? paymentMethod : null;

  const invoicePayAvailable =
    configurationData.invoice_template_available === true;

  React.useEffect(() => {
    if (!invoicePayAvailable && paymentMethod === "invoice") {
      setPaymentMethod("card");
    }
  }, [invoicePayAvailable, paymentMethod]);

  const calculateStripeFee = (
    method: "card" | "us_bank_account" | "check" | "invoice" | null,
    includeInspection = true,
  ): number => {
    const baseSubtotal = totalFee + totalLateFee;
    const currentSubtotal = includeInspection ? subtotal : baseSubtotal;
    if (!method || currentSubtotal <= 0) return 0;
    if (method === "invoice") return 0;

    // Convert subtotal to cents for calculation (since constants are in cents)
    const subtotalInCents = Math.floor(currentSubtotal * 100);

    if (method === "us_bank_account") {
      // ACH fee: subtotal * ACH_FEE_RATE / (1 - ACH_FEE_RATE), capped at $5.00
      const fee = Math.floor(
        (subtotalInCents * ACH_FEE_RATE) / (1 - ACH_FEE_RATE),
      );
      return Math.min(fee, MAX_ACH_FEE_CENTS);
    }

    if (method === "card") {
      // Credit card fee: (subtotal * CREDIT_CARD_FEE_RATE + CREDIT_CARD_FIXED_FEE_CENTS) / (1 - CREDIT_CARD_FEE_RATE)
      return Math.floor(
        (subtotalInCents * CREDIT_CARD_FEE_RATE + CREDIT_CARD_FIXED_FEE_CENTS) /
          (1 - CREDIT_CARD_FEE_RATE),
      );
    }

    return 0;
  };

  // Calculate Stripe fee with and without inspection to determine additional fee
  const stripeFeeWithInspection = calculateStripeFee(
    effectivePaymentMethod,
    true,
  );
  const stripeFeeWithoutInspection = calculateStripeFee(
    effectivePaymentMethod,
    false,
  );
  const inspectionStripeFee = Math.max(
    0,
    stripeFeeWithInspection - stripeFeeWithoutInspection,
  );
  const stripeFee = stripeFeeWithInspection;

  // Check processing fee from HERA config (dollars)
  const checkProcessingFee = React.useMemo(() => {
    const val = feesData?.check_processing_fee;
    if (val == null || val === "") return 0;
    const n = Number.parseFloat(String(val));
    return Number.isNaN(n) ? 0 : n;
  }, [feesData?.check_processing_fee]);

  // Payment fee: Stripe fee for card/ACH, check processing fee for check, $0 for invoice
  const paymentFee = !effectivePaymentMethod
    ? 0
    : effectivePaymentMethod === "check"
      ? checkProcessingFee
      : effectivePaymentMethod === "invoice"
        ? 0
        : stripeFee / 100;
  const totalAmountWithFee = subtotal + paymentFee;

  const submitLockRef = React.useRef(false);
  const [pendingPaymentAction, setPendingPaymentAction] = React.useState<
    | "card"
    | "us_bank_account"
    | "check"
    | "invoice"
    | "pay_later"
    | "no_payment"
    | null
  >(null);
  const paymentSubmitPending = pendingPaymentAction !== null;

  const handlePrevious = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (submitLockRef.current) return;
    onPrevious?.();
  };

  const handleSubmit = async (
    e: React.FormEvent,
    paymentMethodType?:
      | "card"
      | "us_bank_account"
      | "check"
      | "invoice"
      | "pay_later"
      | "no_payment",
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (submitLockRef.current || !paymentMethodType || attestationIncomplete) {
      return;
    }
    submitLockRef.current = true;
    setPendingPaymentAction(paymentMethodType);
    try {
      const errors = await form.validateAllFields("submit");
      if (errors.length === 0) {
        await onSubmit(
          {
            periods: feesToShow.map((fee) => {
              return {
                start_date: fee.start_date,
                end_date: fee.end_date,
                fee: form.store.state.values[`${fee.start_date}-selected`]
                  ? roundToTwoDecimals(
                      form.store.state.values[`${fee.start_date}-fee`],
                    )
                  : "0.00",
                late_fee: form.store.state.values[`${fee.start_date}-selected`]
                  ? roundToTwoDecimals(
                      form.store.state.values[`${fee.start_date}-late-fee`],
                    )
                  : "0.00",
                prefilled_fee: fee.registration_fee,
                prefilled_late_fee: fee.late_fee,
                selected: form.store.state.values[`${fee.start_date}-selected`],
              };
            }),
            registrant_note: form.store.state.values.registrant_note,
            attestation_answers:
              attestationQuestions.length > 0
                ? Object.fromEntries(
                    attestationQuestions
                      .filter((question) => attested[question.id] === true)
                      .map((question) => [question.id, true as const]),
                  )
                : undefined,
            inspection_fee: roundToTwoDecimals(
              form.store.state.values.inspection_fee || "0",
            ),
            // Deferring carries no processor fee, so never bill one for Pay Later.
            inspection_stripe_fee:
              paymentMethodType === "pay_later" ||
              effectivePaymentMethod === "check" ||
              !effectivePaymentMethod
                ? "0"
                : (inspectionStripeFee / 100).toFixed(2),
          },
          paymentMethodType,
        );
      }
    } finally {
      submitLockRef.current = false;
      setPendingPaymentAction(null);
    }
  };

  let errorAndLoadingContent = null;
  if (feesToShow.length === 0) {
    errorAndLoadingContent = (
      <ErrorComponent
        title={isRenewal ? "No fees due" : "No registration periods possible"}
        message={
          isRenewal
            ? "No fees are due for any renewal period."
            : "No periods are available for this license type and jurisdiction."
        }
      />
    );
  }

  const registrationFee = new Number(configurationData?.registration_fee)
    .toFixed(2)
    .replace(".00", "");
  const registrationFee2 = configurationData?.registration_fee_2
    ? new Number(configurationData.registration_fee_2)
        .toFixed(2)
        .replace(".00", "")
    : undefined;
  const registrationFee3 = configurationData?.registration_fee_3
    ? new Number(configurationData.registration_fee_3)
        .toFixed(2)
        .replace(".00", "")
    : undefined;
  const registrationFee4 = configurationData?.registration_fee_4
    ? new Number(configurationData.registration_fee_4)
        .toFixed(2)
        .replace(".00", "")
    : undefined;
  const registrationFee5 = configurationData?.registration_fee_5
    ? new Number(configurationData.registration_fee_5)
        .toFixed(2)
        .replace(".00", "")
    : undefined;
  const firstLateFee = configurationData?.first_late_fee_percentage
    ? `${configurationData.first_late_fee_percentage}% of registration fee`
    : configurationData?.first_late_fee_amount
      ? new Number(configurationData.first_late_fee_amount)
          .toFixed(2)
          .replace(".00", "")
      : "0";
  const recurringLateFee = configurationData?.recurring_late_fee_percentage
    ? `${configurationData.recurring_late_fee_percentage}% of registration fee`
    : configurationData?.recurring_late_fee_amount
      ? new Number(configurationData.recurring_late_fee_amount)
          .toFixed(2)
          .replace(".00", "")
      : "0";
  const formattedTotalFee = new Number(totalFee).toFixed(2).replace(".00", "");
  const formattedTotalLateFee = new Number(totalLateFee)
    .toFixed(2)
    .replace(".00", "");
  const formattedInspectionFee = new Number(inspectionFee)
    .toFixed(2)
    .replace(".00", "");
  const formattedSubtotal = new Number(subtotal).toFixed(2).replace(".00", "");
  const formattedPaymentFee = new Number(paymentFee)
    .toFixed(2)
    .replace(".00", "");
  const formattedTotalAmount = new Number(totalAmountWithFee)
    .toFixed(2)
    .replace(".00", "");
  const noPaymentDue = totalAmountWithFee <= 0;

  // Only show inspection fee when jurisdiction has inspection fees (same logic as fee-information)
  const inspectionFeeStructure = configurationData?.inspection_fee_structure;
  const showInspectionSection =
    inspectionFeeStructure != null &&
    inspectionFeeStructure !== "none" &&
    (configurationData?.inspection_fee != null ||
      configurationData?.inspection_fee_2 != null);

  // Derived number of units when inspection fee is based on units (for display line).
  // Show for both "based_on_units" and "periodic_but_exempt_for_new_properties" (fee = per_unit × units when not exempt).
  const inspectionUnitsWhenBasedOnUnits = React.useMemo(() => {
    const isUnitBased =
      inspectionFeeStructure === "based_on_units" ||
      inspectionFeeStructure === "periodic_but_exempt_for_new_properties";
    if (
      !isUnitBased ||
      configurationData?.inspection_fee == null ||
      Number(configurationData.inspection_fee) <= 0 ||
      feesData?.inspection_fee == null
    ) {
      return null;
    }
    const units = Math.round(
      Number(feesData.inspection_fee) /
        Number(configurationData.inspection_fee),
    );
    return units >= 1 ? units : null;
  }, [
    inspectionFeeStructure,
    configurationData?.inspection_fee,
    feesData?.inspection_fee,
  ]);

  // Calculate exemption end date if inspection is exempt
  const exemptionEndDate = React.useMemo(() => {
    if (
      inspectionFee === 0 &&
      inspectionExemptionAnswer &&
      effectiveDate &&
      configurationData?.inspection_fee_2
    ) {
      try {
        const buildingCompletionDate = parse(
          inspectionExemptionAnswer,
          "yyyy-MM-dd",
          new Date(),
        );
        const exemptionAgeMonths = Number(configurationData.inspection_fee_2);
        if (!Number.isNaN(exemptionAgeMonths)) {
          return addMonths(buildingCompletionDate, exemptionAgeMonths);
        }
      } catch (error) {
        // Invalid date format, return undefined
        return undefined;
      }
    }
    return undefined;
  }, [
    inspectionFee,
    inspectionExemptionAnswer,
    effectiveDate,
    configurationData?.inspection_fee_2,
  ]);

  // Determine if the fee structure is based on property type
  const isStructureFee = configurationData?.fee_structure === "structure";

  // Build a property type fee summary if structure-based
  let propertyTypeFeeSummary = "";
  if (isStructureFee) {
    const propertyTypes = [
      { label: "Residential", value: configurationData?.registration_fee },
      { label: "Multi-Family", value: configurationData?.registration_fee_2 },
      { label: "Vacant Lot", value: configurationData?.registration_fee_3 },
      { label: "Mobile Home", value: configurationData?.registration_fee_4 },
      { label: "Commercial", value: configurationData?.registration_fee_5 },
      { label: "Industrial", value: configurationData?.registration_fee_6 },
    ];
    propertyTypeFeeSummary = propertyTypes
      .filter((pt) => pt.value && pt.value !== "0")
      .map(
        (pt) => `${pt.label}: ${formatCurrency(Number(pt.value).toFixed(2))}`,
      )
      .join(", ");
  }

  /** Format dollar amounts with 2 decimal places (e.g. $75.00) */
  const fmtCurrency = (amount: string | number) => formatCurrency(amount);
  /** Format dollar amounts; omit cents when a whole number (e.g. $75 not $75.00) */
  const fmtCurrencyWhole = (amount: string | number) =>
    formatCurrency(amount, { hideZeroCents: true });

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Step {stepNumber}:{" "}
          {isReapply
            ? "Registration Periods & Payment"
            : isRenewal
              ? "Renewal Periods & Payment"
              : "Registration Periods & Payment"}
        </CardTitle>
        <CardDescription className="space-y-2">
          <p className="mt-2">
            {isReapply
              ? `Based on your ${displayDateLabel.toLowerCase()}, the following registration periods are required.`
              : isRenewal
                ? "Based on your current registration, the following renewal periods are available"
                : `Based on your ${displayDateLabel.toLowerCase()}, the following registration periods are required.`}{" "}
            If, as agent or owner, you are not responsible for paying one of the
            periods, de-select it and provide an explanation.
          </p>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {feesData.omt_exempt && (
          <Alert>
            <AlertDescription>
              <p className="font-medium">
                No registration or late fees are due.
              </p>
              <p className="mt-1 text-sm">
                The party selected as OMT is marked Exempt. You must still
                submit this {isRenewal ? "renewal" : "registration"}. The
                exemption does not waive inspection fees.
              </p>
            </AlertDescription>
          </Alert>
        )}
        {errorAndLoadingContent ? (
          errorAndLoadingContent
        ) : (
          <form.AppForm>
            <form className="space-y-6" onSubmit={handleSubmit}>
              {recapQuestions != null && (
                <FeesPaymentScreenQaBlock
                  questions={recapQuestions}
                  answers={paymentScreenAnswers ?? undefined}
                />
              )}
              <div
                className={
                  recapQuestions != null ? "space-y-4 -mt-3" : "space-y-4"
                }
              >
                {feesToShow.map((fee, index) => {
                  const isLast = index === feesToShow.length - 1;
                  const checkboxId = `${fee.start_date}-selected`;
                  return (
                    <div
                      key={fee.start_date}
                      className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center"
                    >
                      <form.AppField name={`${fee.start_date}-selected`}>
                        {(field) => (
                          <div className="md:col-span-2 md:pt-6">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={checkboxId}
                                onCheckedChange={(checked) => {
                                  field.handleChange(
                                    checked === "indeterminate"
                                      ? true
                                      : checked,
                                  );
                                }}
                                onBlur={field.handleBlur}
                                checked={field.state.value}
                              />
                              <Label htmlFor={checkboxId}>
                                {(() => {
                                  // Use parseDateAsLocal to avoid timezone conversion issues
                                  const start = parseDateAsLocal(
                                    fee.start_date,
                                  );
                                  const end = fee.end_date
                                    ? parseDateAsLocal(fee.end_date)
                                    : null;
                                  return start
                                    ? `${format(start, "MMM d, yyyy")} - ${
                                        end ? format(end, "MMM d, yyyy") : ""
                                      }`
                                    : "Invalid date";
                                })()}
                              </Label>
                            </div>
                            {Number.parseFloat(fee.already_paid_fee ?? "0") >
                              0 && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {fmtCurrency(fee.already_paid_fee ?? "0")}{" "}
                                previously paid
                              </p>
                            )}
                          </div>
                        )}
                      </form.AppField>
                      <form.Subscribe
                        selector={(state) => [
                          state.values[`${fee.start_date}-selected`],
                        ]}
                      >
                        {([isPeriodSelected]) => (
                          <>
                            <form.AppField
                              name={`${fee.start_date}-fee`}
                              validators={{
                                onChange: feeFieldSchema,
                              }}
                            >
                              {(field) => (
                                <FormFieldWrapper
                                  field={field}
                                  label="Registration Fee"
                                >
                                  <FeeAmountInput
                                    value={field.state.value ?? "0"}
                                    disabled={!isPeriodSelected}
                                    onValueChange={(next) =>
                                      field.handleChange(next)
                                    }
                                    onBlurComplete={() => field.handleBlur()}
                                  />
                                </FormFieldWrapper>
                              )}
                            </form.AppField>
                            <form.AppField
                              name={`${fee.start_date}-late-fee`}
                              validators={{
                                onChange: feeFieldSchema,
                              }}
                            >
                              {(field) => (
                                <FormFieldWrapper
                                  field={field}
                                  label="Late Fee"
                                >
                                  <FeeAmountInput
                                    value={field.state.value ?? "0"}
                                    disabled={!isPeriodSelected}
                                    onValueChange={(next) =>
                                      field.handleChange(next)
                                    }
                                    onBlurComplete={() => field.handleBlur()}
                                  />
                                </FormFieldWrapper>
                              )}
                            </form.AppField>
                          </>
                        )}
                      </form.Subscribe>
                      {!isLast && (
                        <Separator className="block md:hidden my-4" />
                      )}
                    </div>
                  );
                })}
              </div>

              {showInspectionSection && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium">
                      Inspection Fee
                    </Label>
                  </div>
                  <form.AppField
                    name="inspection_fee"
                    validators={{
                      onChange: feeFieldSchema,
                    }}
                  >
                    {(field) => (
                      <FormFieldWrapper field={field} label="">
                        <FeeAmountInput
                          value={field.state.value ?? "0"}
                          onValueChange={(next) => field.handleChange(next)}
                          onBlurComplete={() => field.handleBlur()}
                        />
                      </FormFieldWrapper>
                    )}
                  </form.AppField>
                  <div />
                </div>
              )}

              <div className="space-y-0">
                <p className="text-xs text-muted-foreground">
                  {isStructureFee ? (
                    <>
                      The registration fee for each period is based on the
                      property type you selected: {propertyTypeFeeSummary}
                    </>
                  ) : (
                    <>
                      Each {isRenewal ? "renewal" : "registration"} period has a
                      fee of {fmtCurrencyWhole(registrationFee)}.
                      {registrationFee2 && registrationFee2 !== "0" && (
                        <> 2nd: {fmtCurrencyWhole(registrationFee2)} </>
                      )}
                      {registrationFee3 && registrationFee3 !== "0" && (
                        <> 3rd: {fmtCurrencyWhole(registrationFee3)} </>
                      )}
                      {registrationFee4 && registrationFee4 !== "0" && (
                        <> 4th: {fmtCurrencyWhole(registrationFee4)} </>
                      )}
                      {registrationFee5 && registrationFee5 !== "0" && (
                        <> 5th: {fmtCurrencyWhole(registrationFee5)} </>
                      )}
                      {exemptionEndDate && (
                        <>
                          {" "}
                          No inspections required until{" "}
                          {format(exemptionEndDate, "MMM d, yyyy")}.
                        </>
                      )}
                    </>
                  )}
                </p>
                {(configurationData?.first_late_fee_enabled ||
                  configurationData?.recurring_late_fee_enabled) && (
                  <p className="text-xs text-muted-foreground">
                    Late fees are calculated using the following rules:
                  </p>
                )}
                {configurationData?.first_late_fee_enabled && (
                  <p className="text-xs text-muted-foreground pl-4">
                    First late fee:{" "}
                    {configurationData?.first_late_fee_percentage
                      ? firstLateFee
                      : fmtCurrency(firstLateFee)}{" "}
                    (applies after {configurationData.first_late_fee_days} days)
                  </p>
                )}
                {configurationData?.recurring_late_fee_enabled && (
                  <p className="text-xs text-muted-foreground pl-4">
                    Recurring late fee:{" "}
                    {configurationData?.recurring_late_fee_percentage
                      ? recurringLateFee
                      : fmtCurrency(recurringLateFee)}{" "}
                    (applies after {configurationData.recurring_late_fee_days}{" "}
                    days)
                  </p>
                )}
                {configurationData?.late_fee_cap != null && (
                  <p className="text-xs text-muted-foreground pl-4">
                    Late fee cap: {fmtCurrency(configurationData.late_fee_cap)}{" "}
                    (maximum total late fees per period)
                  </p>
                )}
                {inspectionUnitsWhenBasedOnUnits != null &&
                  configurationData?.inspection_fee != null && (
                    <p className="text-xs text-muted-foreground">
                      Inspection Fee{" "}
                      {fmtCurrencyWhole(configurationData.inspection_fee)} per
                      unit for {inspectionUnitsWhenBasedOnUnits} units
                    </p>
                  )}
              </div>

              <div className="p-4 border rounded-md bg-muted">
                <div className="flex justify-between mb-2">
                  <span>
                    {isReapply
                      ? "Registration Fee:"
                      : isRenewal
                        ? "Renewal Fee:"
                        : "Registration Fee:"}
                  </span>
                  <span>{fmtCurrency(formattedTotalFee)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>Late Fee:</span>
                  <span>{fmtCurrency(formattedTotalLateFee)}</span>
                </div>
                {showInspectionSection && (
                  <div className="flex justify-between mb-2">
                    <span>Inspection Fee:</span>
                    <span>{fmtCurrency(formattedInspectionFee)}</span>
                  </div>
                )}
                <div className="flex justify-between mb-2">
                  <span>Subtotal:</span>
                  <span>{fmtCurrency(formattedSubtotal)}</span>
                </div>
                {canMakePayments && !noPaymentDue ? (
                  <div className="flex justify-between mb-2">
                    <span>Payment Fee:</span>
                    <div className="flex items-center gap-4">
                      <RadioGroup
                        value={paymentMethod || ""}
                        disabled={paymentSubmitPending}
                        onValueChange={(value) =>
                          setPaymentMethod(
                            value as
                              | "card"
                              | "us_bank_account"
                              | "check"
                              | "invoice"
                              | null,
                          )
                        }
                        className="flex gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="card" id="card" />
                          <Label htmlFor="card" className="text-xs font-normal">
                            Credit Card
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="us_bank_account" id="ach" />
                          <Label htmlFor="ach" className="text-xs font-normal">
                            ACH
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="check" id="check" />
                          <Label
                            htmlFor="check"
                            className="text-xs font-normal"
                          >
                            Check
                          </Label>
                        </div>
                        {invoicePayAvailable ? (
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="invoice" id="invoice-pay" />
                            <Label
                              htmlFor="invoice-pay"
                              className="text-xs font-normal"
                            >
                              Pay by Invoice
                            </Label>
                          </div>
                        ) : null}
                      </RadioGroup>
                      <span className="min-w-[60px] text-right">
                        {paymentMethod
                          ? fmtCurrency(formattedPaymentFee)
                          : fmtCurrency(0)}
                      </span>
                    </div>
                  </div>
                ) : null}
                <div className="flex justify-between font-medium">
                  <span>Total:</span>
                  <span>{fmtCurrency(formattedTotalAmount)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Note: Any adjustments to fees will be reviewed by an
                  administrator.
                </p>
              </div>

              <form.AppField
                name="registrant_note"
                validators={{
                  onChange: z.string().max(1000),
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Note (optional)">
                    <Textarea
                      placeholder="If you have manually changed fees or deselected periods, please leave a note explaining why for our review team."
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
            </form>
          </form.AppForm>
        )}
        {attestationQuestions.map((question) => {
          const statement =
            question.information_text?.trim() || question.question;
          const showHeading =
            statement !== question.question &&
            !statement.startsWith(question.question);
          return (
            <div
              key={question.id}
              className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3"
            >
              {showHeading ? (
                <p className="text-sm font-medium text-amber-950">
                  {question.question}
                </p>
              ) : null}
              <div className="flex items-start gap-3">
                <Checkbox
                  id={`attestation-${question.id}`}
                  checked={attested[question.id] === true}
                  onCheckedChange={(checked) => {
                    setAttested((current) => ({
                      ...current,
                      [question.id]: checked === true,
                    }));
                  }}
                  className="mt-0.5"
                />
                <Label
                  htmlFor={`attestation-${question.id}`}
                  className="text-sm font-normal leading-snug text-amber-950"
                >
                  {statement}
                </Label>
              </div>
            </div>
          );
        })}
        <Alert variant="warning">
          <AlertDescription>
            <p className="text-sm font-medium">
              LEGAL NOTICE: By submitting this{" "}
              {isRenewal ? "renewal" : "registration"}, I certify that I am the
              property owner or authorized agent and that all information
              provided is true and correct to the best of my knowledge. I
              understand that providing false information may result in
              penalties.
            </p>
          </AlertDescription>
        </Alert>
      </CardContent>
      <CardFooter
        className={`flex ${onPrevious ? "justify-between" : "justify-end"}`}
      >
        {onPrevious && (
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={paymentSubmitPending}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>
        )}
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => {
            const paymentBusy = paymentSubmitPending || Boolean(isSubmitting);
            const payLabel =
              paymentMethod === "us_bank_account"
                ? "ACH"
                : paymentMethod === "check"
                  ? "Check"
                  : paymentMethod === "invoice"
                    ? "Invoice"
                    : "Credit Card";
            return (
              <div className="flex gap-2">
                {noPaymentDue ? (
                  <Button
                    onClick={(e) => handleSubmit(e, "no_payment")}
                    aria-busy={pendingPaymentAction === "no_payment"}
                    disabled={
                      !canSubmit ||
                      numSelectedPeriods === 0 ||
                      !feesData ||
                      paymentBusy ||
                      attestationIncomplete
                    }
                  >
                    {pendingPaymentAction === "no_payment" ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        {isRenewal ? "Submit Renewal" : "Submit Registration"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={(e) => handleSubmit(e, "pay_later")}
                      aria-busy={pendingPaymentAction === "pay_later"}
                      disabled={
                        !canSubmit ||
                        numSelectedPeriods === 0 ||
                        !feesData ||
                        paymentBusy ||
                        attestationIncomplete
                      }
                    >
                      {pendingPaymentAction === "pay_later" ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "Pay Later"
                      )}
                    </Button>
                    {canMakePayments ? (
                      <Button
                        className="disabled:opacity-20 disabled:cursor-not-allowed"
                        onClick={(e) =>
                          handleSubmit(e, paymentMethod || undefined)
                        }
                        aria-busy={
                          pendingPaymentAction !== null &&
                          pendingPaymentAction !== "pay_later"
                        }
                        disabled={
                          !canSubmit ||
                          numSelectedPeriods === 0 ||
                          !feesData ||
                          paymentBusy ||
                          !paymentMethod ||
                          attestationIncomplete
                        }
                      >
                        {pendingPaymentAction !== null &&
                        pendingPaymentAction !== "pay_later" ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            {`Pay by ${payLabel}`}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    ) : null}
                  </>
                )}
              </div>
            );
          }}
        </form.Subscribe>
      </CardFooter>
    </Card>
  );
}
